/**
 * La carte de la navigation du gestionnaire (Yros/Kavero#416) : sections,
 * entrées et onglets, en un seul endroit. La barre latérale, le fil d'Ariane
 * et les écrans à onglets la lisent tous : un onglet ajouté ici apparaît
 * partout à la fois, et un lien sait quel onglet il ouvre.
 */
import { FaAddressBook, FaClock, FaGavel, FaReceipt, FaRegCalendarCheck, FaSitemap, FaUserClock } from 'react-icons/fa';
import { HiBanknotes, HiCalendarDays, HiUserGroup } from 'react-icons/hi2';

import { Doc2Icon, DocIcon, HomeIcon, LeaveIcon, MoneyIcon, PeopleIcon, StateIcon } from '../Tools/IconSvg';
import {
  aOffreRh,
  canAccessSettings,
  canConfigurerPointage,
  canConsulterAnnuaire,
  canConsulterOrganigramme,
  canConsulterBulletins,
  canConsulterCollaborateurs,
  canConsulterEtats,
  canEmettreOrdresMission,
  canGererDeparts,
  canGererDossiersDisciplinaires,
  canGererAbsences,
  canGererDroitsRh,
  canGererElementsRemuneration,
  canGererOffres,
  canImportPayroll,
  canManageAccountingSetup,
  canManageCompany,
  canManageUsers,
  canSuperviserDemandesRh,
  canSuperviserFeuillesTemps,
  canSuperviserPresences,
  canValiderHeuresSup,
} from '../Auth/permissions';

// Les onglets de chaque écran, dans l'ordre de l'écran. Sans `?onglet=` dans
// l'adresse — ou avec un onglet inconnu, ou refusé —, c'est le premier
// autorisé qui s'ouvre. `autorise` n'est qu'un confort d'interface : la
// barrière reste côté Odoo.
const ONGLETS = {
  '/leave': [
    { id: 'absences', cle: 'leave.absencesAndLeave', Icone: LeaveIcon, motsCles: ['congé', 'absence', 'maladie', 'permission', 'solde', 'leave'] },
    { id: 'hsp', cle: 'leave.overtimeHours', Icone: FaClock, motsCles: ['heures sup', 'hs', 'hsp', 'overtime'] },
    // Yros/Kavero#491 : les heures sup calculées au pointage, à valider.
    { id: 'heures_sup_pointage', cle: 'leave.heuresSupPointage', Icone: FaClock, autorise: canValiderHeuresSup, motsCles: ['heures sup', 'pointage', 'valider', 'overtime'] },
    { id: 'presence_du_jour', cle: 'sidebar.presences', Icone: FaUserClock, autorise: canSuperviserPresences, motsCles: ['pointage', 'présent', 'retard', 'attendance'] },
  ],
  '/primes': [
    { id: 'pimp', cle: 'primesPage.taxablePrime', motsCles: ['prime', 'bonus', 'indemnité'] },
    { id: 'pnoimp', cle: 'primesPage.nonTaxablePrime', motsCles: ['prime', 'transport', 'indemnité'] },
    { id: 'advantages', cle: 'primesPage.benefitsInKind', motsCles: ['avantage', 'logement', 'véhicule'] },
    { id: 'deduction', cle: 'primesPage.salaryDeductions', motsCles: ['retenue', 'prêt', 'saisie', 'deduction'] },
    // Pas « note » : les notes de frais déposées par les salariés vivent au RH.
    { id: 'notes', cle: 'primesPage.expenseReport', motsCles: ['remboursement', 'frais'] },
    // Les bornes d'une opération se règlent là où l'opération se saisit
    // (décision du 18/09/2026, Yros/Kavero#411).
    // Paramètres des demandes d'avance, d'acompte et de prêt des salariés :
    // un ajout du RH, absent d'une offre Paie seule (YRH-FOUND-014).
    { id: 'avances', cle: 'parametresAvances.onglet', autorise: aOffreRh, motsCles: ['borne', 'plafond', 'avance', 'acompte', 'prêt'] },
  ],
  // Consulter, importer, synchroniser : l'alimentation vient après la
  // consultation, la synchro après l'import qu'elle remplacera. L'écran
  // n'est pas traduit, ses libellés non plus.
  '/paie/rh': [
    { id: 'lignes', libelle: 'Lignes de paie', motsCles: ['pivot'] },
    { id: 'import', libelle: 'Import de paie', motsCles: ['csv', 'fichier'] },
    { id: 'sync', libelle: 'Synchronisation paie', motsCles: ['synchro'] },
  ],
  '/settings': [
    { id: 'societe', cle: 'settingsPage.company', autorise: canManageCompany, motsCles: ['entreprise', 'logo', 'adresse'] },
    // Dépendance historique à multicompany : la permission seule montrerait
    // la liste à un admin mono-société, qui ne la voyait pas.
    { id: 'users_list', cle: 'settingsPage.usersAccess', autorise: (user) => canManageUsers(user) && Boolean(user.multicompany), motsCles: ['accès', 'droits', 'rôle'] },
    // Qui peut corriger les pointages (YRH-CORE-013) : un ajout du RH, ouvert à
    // l'admin d'une société seule aussi — l'onglet ci-dessus ne l'est pas, et
    // n'aurait jamais permis de donner le droit.
    { id: 'acces_droits', cle: 'settingsPage.accesDroits', autorise: canGererDroitsRh, motsCles: ['accès', 'droits', 'correction', 'corriger', 'pointage', 'permission'] },
    // Son propre profil : ouvert à qui entre.
    { id: 'utilisateur', cle: 'settingsPage.myProfile', motsCles: ['profil', 'mot de passe'] },
    { id: 'comptabilite', cle: 'settingsPage.accounting', autorise: canManageAccountingSetup, motsCles: ['compte', 'comptable', 'journal', 'écriture'] },
    { id: 'configuration_pointage', cle: 'settingsPage.pointage', autorise: canConfigurerPointage, motsCles: ['géolocalisation', 'lieu', 'badge', 'configuration', 'horaires', 'heures supplémentaires', 'heures sup', 'tolérance'] },
    // L'offre de chaque société, choisie par le superadmin Payor
    // (Yros/Kavero#447) — jamais par un admin client, et sans regarder l'offre
    // de la société active : c'est depuis une société en Paie seule qu'on
    // ouvre le RH d'une autre.
    { id: 'offres', cle: 'settingsPage.offres', autorise: canGererOffres, motsCles: ['offre', 'abonnement', 'rh', 'paie', 'activer', 'couper', 'société'] },
  ],
};

/**
 * Les onglets d'un écran que le compte voit. Tant que l'utilisateur n'est
 * pas résolu, aucun onglet gardé : le défaut est fermé.
 */
export const ongletsDe = (chemin, user, t) =>
  (ONGLETS[chemin] || [])
    .filter(({ autorise }) => !autorise || (user ? autorise(user) : false))
    .map(({ id, cle, libelle, Icone, motsCles = [] }) => ({ id, label: cle ? t(cle) : libelle, Icone, motsCles }));

/** L'onglet ouvert : celui de l'adresse s'il est permis, sinon le premier. */
export const ongletOuvert = (onglets, demande) =>
  onglets.some(({ id }) => id === demande) ? demande : onglets[0]?.id;

export const lienDe = ({ chemin, onglet }) => (onglet ? `${chemin}?onglet=${onglet}` : chemin);

// Une page couvre ses sous-pages (`/employees/12`), jamais un voisin qui
// partage un préfixe de mot (`/paie/journaliers` n'est pas sous `/paie/rh`).
const couvre = (chemin, pathname) =>
  chemin === '/' ? pathname === '/' : pathname === chemin || pathname.startsWith(`${chemin}/`);

const ongletDemande = (search) => new URLSearchParams(search).get('onglet');

/**
 * Sections du menu, dans l'ordre d'un SIRH classique : l'accueil et les
 * collaborateurs, le temps et les absences, la Paie — socle de tout client —,
 * le RH. Chaque entrée vit sous sa section, pour que le masquage par offre
 * (Yros/Kavero#332) n'ait qu'une section à retirer. Une section sans entrée
 * autorisée disparaît. Le pied (guide, paramètres) garde son propre rendu
 * dans la barre latérale ; il figure ici pour le fil d'Ariane.
 */
export const construireNavigation = (user, t) => [
  {
    id: 'accueil',
    entrees: [
      { id: 'tableau-de-bord', chemin: '/', libelle: t('sidebar.dashboard'), Icone: HomeIcon, motsCles: ['accueil', 'dashboard'] },
      ...(canConsulterCollaborateurs(user)
        ? [{ id: 'collaborateurs', chemin: '/employees', libelle: t('sidebar.employees'), Icone: PeopleIcon, motsCles: ['salarié', 'employé', 'personnel', 'fiche', 'effectif'] }]
        : []),
      // Yros/Kavero#199 : l'annuaire interne, pour tout rôle avec l'offre RH.
      ...(canConsulterAnnuaire(user)
        ? [{ id: 'annuaire', chemin: '/annuaire', libelle: t('sidebar.annuaire'), Icone: FaAddressBook, motsCles: ['annuaire', 'collègue', 'contact', 'téléphone', 'présent', 'trombinoscope'] }]
        : []),
      // Yros/Kavero#494 : l'organigramme de la société, pour la RH.
      ...(canConsulterOrganigramme(user)
        ? [{ id: 'organigramme', chemin: '/organigramme', libelle: t('sidebar.organigramme'), Icone: FaSitemap, motsCles: ['organigramme', 'hiérarchie', 'responsable', 'manager', 'équipe'] }]
        : []),
    ],
  },
  {
    id: 'temps',
    Icone: HiCalendarDays,
    titre: t('sidebar.sections.temps'),
    // Les onglets de l'écran des présences sont des entrées à part entière :
    // la RH n'a plus à deviner dans quel écran chercher les types de congé.
    // Les définitions d'`ONGLETS` ne changent pas : `Leave.js` ne doit pas voir
    // ses onglets disparaître le temps que l'utilisateur se résolve.
    entrees: [
      ...(canGererAbsences(user)
        ? ongletsDe('/leave', user, t).map(({ id, label, Icone, motsCles }) => ({
          id: `leave-${id}`, chemin: '/leave', onglet: id, libelle: label, Icone, motsCles,
        }))
        : []),
      // Les feuilles de la semaine que les salariés envoient, et leurs heures
      // sup à décider (Yros/Kavero#220) : un ajout du RH, absent en Paie seule.
      ...(canSuperviserFeuillesTemps(user)
        ? [{ id: 'feuilles-temps', chemin: '/rh/feuilles-temps', libelle: t('sidebar.feuillesTemps'), Icone: FaRegCalendarCheck, motsCles: ['feuille de temps', 'timesheet', 'semaine', 'heures sup', 'pointage', 'renvoyer'], pastille: 'heures_sup' }]
        : []),
    ],
  },
  {
    id: 'paie',
    Icone: HiBanknotes,
    titre: t('sidebar.sections.paie'),
    entrees: [
      ...(canGererElementsRemuneration(user)
        ? [{ id: 'elements-variables', chemin: '/primes', libelle: t('sidebar.primes'), Icone: MoneyIcon, motsCles: ['prime', 'avantage', 'déduction', 'variable'] }]
        : []),
      ...(canConsulterBulletins(user)
        ? [{ id: 'bulletins', chemin: '/payslip/report', libelle: t('sidebar.payslip'), Icone: Doc2Icon, motsCles: ['bulletin', 'fiche de paie', 'clôture', 'calcul', 'payslip'] }]
        : []),
      ...(canConsulterEtats(user)
        ? [{ id: 'etats', chemin: '/etats', libelle: t('sidebar.states'), Icone: StateIcon, motsCles: ['déclaration', 'cnss', 'cnps', 'ipres', 'css', 'ipm', 'cotisation', 'impôt', 'livre de paie', 'rapport'] }]
        : []),
    ],
  },
  {
    id: 'rh',
    Icone: HiUserGroup,
    titre: t('sidebar.sections.rh'),
    entrees: [
      ...(canSuperviserDemandesRh(user)
        ? [
          // `pastille` : le compteur « À traiter » que l'entrée affiche. La
          // supervision liste tous les types sauf les heures sup, qui se
          // décident dans « Feuilles de temps ».
          { id: 'demandes', chemin: '/rh/supervision', libelle: t('sidebar.supervisionRh'), Icone: DocIcon, motsCles: ['avance', 'acompte', 'prêt', 'demande', 'validation', 'approuver'], pastille: 'supervision' },
          { id: 'circuits-validation', chemin: '/rh/circuits-validation', libelle: t('sidebar.circuitsValidation'), Icone: DocIcon, motsCles: ['circuit', 'validation', 'manager', 'approbation', 'workflow'] },
          { id: 'notes-frais', chemin: '/rh/notes-frais', libelle: t('sidebar.notesFrais'), Icone: FaReceipt, motsCles: ['note de frais', 'justificatif', 'dépense', 'expense'], pastille: 'note_frais' },
        ]
        : []),
      ...(canEmettreOrdresMission(user)
        ? [{ id: 'ordres-mission', chemin: '/rh/ordres-mission', libelle: t('sidebar.ordresMission'), Icone: Doc2Icon, motsCles: ['mission', 'déplacement'] }]
        : []),
      ...(canGererDeparts(user)
        ? [{ id: 'departs', chemin: '/rh/departs', libelle: t('sidebar.departs'), Icone: Doc2Icon, motsCles: ['départ', 'offboarding', 'solde de tout compte', 'STC', 'démission', 'licenciement'] }]
        : []),
      ...(canGererDossiersDisciplinaires(user)
        ? [{ id: 'discipline', chemin: '/rh/discipline', libelle: t('sidebar.discipline'), Icone: FaGavel, motsCles: ['discipline', 'sanction', 'convocation', 'avertissement', 'dossier'] }]
        : []),
      // Les lignes de paie du pivot RH : import et synchronisation.
      ...(canImportPayroll(user)
        ? [{ id: 'donnees-paie', chemin: '/paie/rh', libelle: t('sidebar.payrollRh'), Icone: MoneyIcon, motsCles: ['import', 'synchronisation', 'pivot'] }]
        : []),
    ],
  },
  {
    id: 'pied',
    entrees: [
      { id: 'guide', chemin: '/guide', libelle: t('sidebar.userGuide'), motsCles: ['aide', 'documentation', 'manuel'] },
      ...(canAccessSettings(user)
        ? [{ id: 'parametres', chemin: '/settings', libelle: t('sidebar.settings'), motsCles: ['réglages', 'configuration'] }]
        : []),
    ],
  },
].filter(({ entrees }) => entrees.length > 0);

/**
 * Une entrée est allumée sur sa page et ses sous-pages ; une entrée qui est
 * un onglet, seulement quand c'est son onglet qui est ouvert.
 */
export const estActive = (entree, { pathname, search }, user, t) => {
  if (!couvre(entree.chemin, pathname)) return false;
  if (!entree.onglet) return true;
  return ongletOuvert(ongletsDe(entree.chemin, user, t), ongletDemande(search)) === entree.onglet;
};

/**
 * Le fil d'Ariane suit la barre latérale : la section, l'entrée, puis
 * l'onglet ouvert quand l'écran n'est pas déjà découpé en entrées —
 * « Paie › Éléments variables › Déductions sur salaire ». Une miette seule
 * qui est la page même répète le titre de l'écran : pas de fil.
 */
export const filAriane = (location, user, t) => {
  for (const section of construireNavigation(user, t)) {
    const entree = section.entrees.find((candidate) => estActive(candidate, location, user, t));
    if (!entree) continue;

    const surLaPage = location.pathname === entree.chemin;
    const onglets = surLaPage && !entree.onglet ? ongletsDe(entree.chemin, user, t) : [];
    const ouvert = onglets.find(({ id }) => id === ongletOuvert(onglets, ongletDemande(location.search)));
    const miettes = [
      ...(section.titre ? [{ libelle: section.titre }] : []),
      { libelle: entree.libelle, chemin: lienDe(entree), courante: surLaPage && !ouvert },
      ...(ouvert ? [{ libelle: ouvert.label, courante: true }] : []),
    ];
    return miettes.length < 2 && surLaPage ? [] : miettes;
  }
  return [];
};

/**
 * Tout ce que la recherche globale peut ouvrir : les entrées du menu, puis
 * les onglets des écrans qui ne sont pas déjà découpés en entrées. Chaque
 * destination porte son contexte (« Paie › Éléments variables ») et les mots
 * du métier qui y mènent (IPM, prêt, congé). Rien de ce que le compte ne voit
 * pas au menu.
 */
export const destinationsDe = (user, t) =>
  construireNavigation(user, t).flatMap((section) =>
    section.entrees.flatMap((entree) => {
      const page = {
        id: entree.id,
        type: 'page',
        libelle: entree.libelle,
        contexte: section.titre || '',
        lien: lienDe(entree),
        motsCles: entree.motsCles || [],
      };
      if (entree.onglet) return [page];
      const contexte = [section.titre, entree.libelle].filter(Boolean).join(' › ');
      return [
        page,
        ...ongletsDe(entree.chemin, user, t).map((onglet) => ({
          id: `${entree.id}-${onglet.id}`,
          type: 'onglet',
          libelle: onglet.label,
          contexte,
          lien: lienDe({ chemin: entree.chemin, onglet: onglet.id }),
          motsCles: onglet.motsCles,
        })),
      ];
    })
  );
